import { DateTime } from "luxon";

import {
  IPricingGroupFigures,
  PricingAlignTarget,
  PricingGroupKey,
} from "../../models/pricing/IPricing";
import {
  DEFAULT_GRADE_B_MARKDOWN_PERCENT,
  DEFAULT_GRADE_C_ONWARDS_MARKDOWN_PERCENT,
  gradeAdjustedPrices,
} from "../../utils/gradePricing";
import formatDateTime from "../../utils/formatDateTime";
import PriceFormat from "../../utils/priceFormat";
import { formatPriceRange, formatSpread, groupMarketDelta, marketGapCaption, MARKET_TONE_CLASSES } from "../../utils/pricingGroups";
import classNames from "../../utils/classNames";
import Drawer from "../../layouts/Drawer";
import { ButtonInput } from "../shared/Inputs/ButtonInput";

export interface IPricingDrawerGroup {
  key: PricingGroupKey;
  title: string;
  subtitle: string | null;
  figures: IPricingGroupFigures;
}

interface IPricingGroupDrawerProps {
  group: IPricingDrawerGroup | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  gradeB?: number;
  gradeC?: number;
  busy: boolean;
  busyKind: "intel" | "align" | null;
  onGetIntelligent: (key: PricingGroupKey) => void;
  onAlign: (key: PricingGroupKey, target: PricingAlignTarget) => void;
}

/**
 * Finance and grade scales for one price-together group, with the align actions.
 */
export default function PricingGroupDrawer({
  group,
  open,
  setOpen,
  gradeB = DEFAULT_GRADE_B_MARKDOWN_PERCENT,
  gradeC = DEFAULT_GRADE_C_ONWARDS_MARKDOWN_PERCENT,
  busy,
  busyKind,
  onGetIntelligent,
  onAlign,
}: IPricingGroupDrawerProps) {
  const figures = group?.figures;
  const soldPrices = figures ? gradeAdjustedPrices(figures.averageSoldPrice, figures.grades, gradeB, gradeC) : [];
  const intelPrices = figures ? gradeAdjustedPrices(figures.intelligenceAverage, figures.grades, gradeB, gradeC) : [];
  const hasSold = figures != null && figures.averageSoldPrice != null && figures.averageSoldPrice > 0;
  const hasIntel = figures != null && figures.intelligenceAverage != null && figures.intelligenceAverage > 0;
  const delta = figures ? groupMarketDelta(figures) : null;

  return (
    <Drawer open={open} setOpen={(value) => setOpen(typeof value === "function" ? value(open) : value)} title={group?.title ?? "Group"} maxWidth="max-w-lg">
      {group && figures && (
        <div className="flex flex-col gap-6">
          {group.subtitle && <p className="-mt-2 text-sm text-gray-500">{group.subtitle}</p>}

          {delta && (
            <span className={classNames("inline-flex w-fit rounded-full px-2.5 py-1 text-xs font-semibold", MARKET_TONE_CLASSES[delta.tone])}>
              {marketGapCaption(delta)}
              {delta.basis ? ` vs ${delta.basis}` : ""}
            </span>
          )}

          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Finance</h3>
            <dl className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-200">
              <FinanceRow label="On eBay" value={figures.listedCount.toLocaleString("en-GB")} />
              <FinanceRow label="In stock" value={figures.stockCount.toLocaleString("en-GB")} />
              <FinanceRow
                label="Current prices"
                value={formatPriceRange({ minPrice: figures.minPrice, maxPrice: figures.maxPrice })}
                hint={figures.averagePrice != null ? `avg ${PriceFormat(figures.averagePrice)}` : undefined}
              />
              <FinanceRow label="Spread" value={formatSpread(figures.spread)} />
              <FinanceRow
                label="Average sold"
                value={hasSold ? PriceFormat(figures.averageSoldPrice) : "—"}
                hint={figures.soldCount > 0 ? `${figures.soldCount.toLocaleString("en-GB")} sold` : undefined}
              />
              <FinanceRow
                label="Intelligent"
                value={hasIntel ? PriceFormat(figures.intelligenceAverage) : "Not fetched"}
                hint={
                  figures.lastIntelligenceLookup
                    ? formatDateTime(figures.lastIntelligenceLookup, DateTime.DATETIME_SHORT)
                    : figures.intelligenceCount > 0
                      ? `${figures.intelligenceCount.toLocaleString("en-GB")} looked up`
                      : undefined
                }
              />
            </dl>
          </section>

          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Grade scales</h3>
            <p className="mt-1 text-xs text-gray-500">
              Grade A stays at the base. B is {gradeB}% lower. C onwards is {gradeC}% lower.
            </p>
            {soldPrices.length === 0 && intelPrices.length === 0 ? (
              <p className="mt-2 text-sm text-gray-500">Fetch intelligent prices or wait for a sold figure to see the scale.</p>
            ) : (
              <table className="mt-2 min-w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    <th className="py-1.5 pr-3">Grade</th>
                    <th className="py-1.5 pr-3 text-right">From avg sold</th>
                    <th className="py-1.5 text-right">From intelligent</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(soldPrices.length > 0 ? soldPrices : intelPrices).map((row) => {
                    const intel = intelPrices.find((item) => item.letter === row.letter);
                    const sold = soldPrices.find((item) => item.letter === row.letter);
                    return (
                      <tr key={row.letter}>
                        <td className="py-1.5 pr-3 font-medium text-gray-800">{row.label}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums text-gray-900">{sold ? PriceFormat(sold.price) : "—"}</td>
                        <td className="py-1.5 text-right tabular-nums text-gray-900">{intel ? PriceFormat(intel.price) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Actions</h3>
            <ButtonInput
              label="Get intelligent"
              onClick={() => onGetIntelligent(group.key)}
              disabled={busy}
              isLoading={busy && busyKind === "intel"}
              size="sm"
              classes="w-full"
            />
            <ButtonInput
              label="Align price"
              onClick={() => onAlign(group.key, PricingAlignTarget.AverageSold)}
              disabled={busy || !hasSold}
              isLoading={false}
              size="sm"
              classes="w-full"
            />
            <ButtonInput
              label="Align price and grade"
              onClick={() => onAlign(group.key, PricingAlignTarget.AverageSoldGradeMarkdown)}
              disabled={busy || !hasSold}
              isLoading={false}
              size="sm"
              classes="w-full"
            />
            <ButtonInput
              label="Set to intelligent"
              onClick={() => onAlign(group.key, PricingAlignTarget.Intelligent)}
              disabled={busy || !hasIntel}
              isLoading={false}
              size="sm"
              classes="w-full"
            />
            <ButtonInput
              label="Apply intelligent markdowns"
              onClick={() => onAlign(group.key, PricingAlignTarget.GradeMarkdown)}
              disabled={busy || !hasIntel}
              isLoading={false}
              size="sm"
              classes="w-full"
            />
          </section>
        </div>
      )}
    </Drawer>
  );
}

function FinanceRow({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 px-3 py-2">
      <dt className="text-sm text-gray-500">{label}</dt>
      <dd className="text-right">
        <div className="text-sm font-medium tabular-nums text-gray-900">{value}</div>
        {hint && <div className="text-xs text-gray-500">{hint}</div>}
      </dd>
    </div>
  );
}
